import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import EntityHistory from '../components/common/EntityHistory';
import Pagination from '../components/common/Pagination';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';

const TYPES     = ['cro','synthesis','method_development','analytical','regulatory','other'];
const STATUSES  = ['proposed','active','on_hold','completed','frozen'];
const PRIORITIES = ['low','medium','high','critical'];
const CURRENCIES = ['INR','USD','EUR','GBP'];
const UNITS     = ['mg','g','kg','ml','l','m','cm','mm','units','pcs','boxes','bottles'];

const statusColors = { active:'success', proposed:'info', on_hold:'warning', completed:'neutral', frozen:'error' };

const FreezeIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07"/>
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const EyeIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const cap = (s) => (s || '').replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase());

function ProjectFormModal({ open, onClose, onSaved, initial, users }) {
  const isEdit = !!initial;
  const blank = { title:'', type:'cro', status:'proposed', priority:'medium',
                  start_date:'', end_date:'', budget:'', currency:'INR', assigned_to:'', deliverables:'', notes:'',
                  qty:'', qty_unit:'units', cat_no:'', batch_no:'', cas_no:'' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          title: initial.title || '',
          type: initial.type || 'cro',
          status: initial.status || 'proposed',
          priority: initial.priority || 'medium',
          start_date: initial.start_date ? initial.start_date.slice(0,10) : '',
          end_date: initial.end_date ? initial.end_date.slice(0,10) : '',
          budget: initial.budget || '',
          currency: initial.currency || 'INR',
          assigned_to: initial.assigned_to || '',
          deliverables: initial.deliverables || '',
          notes: initial.notes || '',
          qty: initial.qty || '',
          qty_unit: initial.qty_unit || 'units',
          cat_no: initial.cat_no || '',
          batch_no: initial.batch_no || '',
          cas_no: initial.cas_no || '',
        });
      } else {
        setForm(blank);
      }
      setErr('');
    }
  }, [open, initial]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.title.trim()) { setErr('Title is required.'); return; }
    if (!form.type) { setErr('Type is required.'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form };
      if (!payload.budget) payload.budget = null;
      if (!payload.assigned_to) payload.assigned_to = null;
      if (!payload.start_date) payload.start_date = null;
      if (!payload.end_date) payload.end_date = null;
      if (!payload.qty) payload.qty = null;
      if (!payload.qty_unit) payload.qty_unit = null;
      if (!payload.cat_no) payload.cat_no = null;
      if (!payload.batch_no) payload.batch_no = null;
      if (!payload.cas_no) payload.cas_no = null;
      if (isEdit) {
        await api.put(`/projects/${initial.id}`, payload);
      } else {
        await api.post('/projects', payload);
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? `Edit Project — ${initial?.project_number}` : 'New Project'} size="lg"
      footer={
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Project'}
          </button>
        </div>
      }
    >
      {err && <div className="alert alert-error" style={{ marginBottom:12 }}>{err}</div>}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px 16px' }}>
        <div style={{ gridColumn:'1/-1' }}>
          <label className="form-label">Title *</label>
          <input className="form-control" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Project title" />
        </div>
        <div>
          <label className="form-label">Type *</label>
          <select className="form-control form-select" value={form.type} onChange={e => set('type', e.target.value)}>
            {TYPES.map(t => <option key={t} value={t}>{cap(t)}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Status</label>
          <select className="form-control form-select" value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.filter(s => s !== 'frozen').map(s => <option key={s} value={s}>{cap(s)}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Priority</label>
          <select className="form-control form-select" value={form.priority} onChange={e => set('priority', e.target.value)}>
            {PRIORITIES.map(p => <option key={p} value={p}>{cap(p)}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Start Date</label>
          <input type="date" className="form-control" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
        </div>
        <div>
          <label className="form-label">End Date</label>
          <input type="date" className="form-control" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
        </div>
        <div>
          <label className="form-label">Budget</label>
          <div style={{ display:'flex', gap:6 }}>
            <select className="form-control form-select" style={{ width:90 }} value={form.currency} onChange={e => set('currency', e.target.value)}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" className="form-control" value={form.budget} onChange={e => set('budget', e.target.value)} placeholder="0.00" style={{ flex:1 }} />
          </div>
        </div>
        <div>
          <label className="form-label">Assigned To</label>
          <select className="form-control form-select" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
            <option value="">Unassigned</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">QTY</label>
          <div style={{ display:'flex', gap:6 }}>
            <input type="number" className="form-control" value={form.qty} onChange={e => set('qty', e.target.value)} placeholder="Quantity" style={{ flex:1 }} />
            <select className="form-control form-select" style={{ width:90 }} value={form.qty_unit} onChange={e => set('qty_unit', e.target.value)}>
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="form-label">Cat No</label>
          <input className="form-control" value={form.cat_no} onChange={e => set('cat_no', e.target.value)} placeholder="Catalog number" />
        </div>
        <div>
          <label className="form-label">Batch No</label>
          <input className="form-control" value={form.batch_no} onChange={e => set('batch_no', e.target.value)} placeholder="Batch number" />
        </div>
        <div>
          <label className="form-label">CAS No</label>
          <input className="form-control" value={form.cas_no} onChange={e => set('cas_no', e.target.value)} placeholder="CAS number" />
        </div>
        <div style={{ gridColumn:'1/-1' }}>
          <label className="form-label">Deliverables</label>
          <textarea className="form-control" rows={2} value={form.deliverables} onChange={e => set('deliverables', e.target.value)} placeholder="Key deliverables…" />
        </div>
        <div style={{ gridColumn:'1/-1' }}>
          <label className="form-label">Notes</label>
          <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes…" />
        </div>
      </div>
    </Modal>
  );
}

function FreezeModal({ open, project, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const isFrozen = project?.frozen_at;

  const handleAction = async () => {
    setLoading(true);
    try {
      if (isFrozen) {
        await api.post(`/projects/${project.id}/unfreeze`);
      } else {
        await api.post(`/projects/${project.id}/freeze`);
      }
      onDone();
      onClose();
    } catch (e) {
      alert(e.response?.data?.message || 'Action failed.');
    } finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={isFrozen ? 'Unfreeze Project' : 'Freeze Project'} size="sm"
      footer={
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className={`btn ${isFrozen ? 'btn-primary' : 'btn-danger'}`} onClick={handleAction} disabled={loading}>
            {loading ? 'Processing…' : isFrozen ? 'Unfreeze' : 'Freeze Project'}
          </button>
        </div>
      }
    >
      {project && (
        <div style={{ fontSize:'0.9rem', color:'var(--text-secondary)', lineHeight:1.6 }}>
          {isFrozen ? (
            <>Unfreeze <strong>{project.project_number} — {project.title}</strong>? This will allow edits again and set status back to <strong>Active</strong>.</>
          ) : (
            <>Freeze <strong>{project.project_number} — {project.title}</strong>? Frozen projects cannot be edited until unfrozen.</>
          )}
          {isFrozen && project.frozen_by_name && (
            <div style={{ marginTop:8, fontSize:'0.8rem', color:'var(--text-muted)' }}>
              Frozen by {project.frozen_by_name} on {fmtDate(project.frozen_at)}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function ProjectAssignmentsSummary({ projectId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/assignments`)
      .then(r => setItems(r.data.data || []))
      .catch(() => {});
  }, [projectId]);

  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 6 }}>Also Assigned To</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {items.map(a => (
          <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, background: a.access_level === 'read_write' ? 'var(--primary-lighter)' : 'var(--surface)', border: '1px solid var(--border)', fontSize: '0.78rem' }}>
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            {a.user_name}
            <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginLeft: 2 }}>
              {a.access_level === 'read_write' ? 'RW' : 'RO'}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

const MS_STATUS = {
  pending:     { label:'Pending',     bg:'#f1f5f9', color:'#64748b' },
  in_progress: { label:'In Progress', bg:'#dbeafe', color:'#1e40af' },
  completed:   { label:'Done',        bg:'#d1fae5', color:'#065f46' },
  blocked:     { label:'Blocked',     bg:'#fee2e2', color:'#dc2626' },
};

const BLANK_MS = { title:'', description:'', due_date:'', status:'pending', completion_pct:0 };

function ViewModal({ project, onClose, onEdit, onFreeze, canEdit, canEditMilestones }) {
  const [milestones, setMilestones] = useState([]);
  const [msLoading, setMsLoading]   = useState(false);
  const [showMsForm, setShowMsForm] = useState(false);
  const [editMs, setEditMs]         = useState(null); // milestone being edited
  const [msForm, setMsForm]         = useState(BLANK_MS);
  const [msSaving, setMsSaving]     = useState(false);

  const loadMs = useCallback(async () => {
    if (!project) return;
    setMsLoading(true);
    try {
      const { data: r } = await api.get(`/projects/${project.id}/milestones`);
      setMilestones(r.data || []);
    } finally { setMsLoading(false); }
  }, [project]);

  useEffect(() => { loadMs(); }, [loadMs]);

  const openAddMs = () => { setEditMs(null); setMsForm(BLANK_MS); setShowMsForm(true); };
  const openEditMs = (m) => {
    setEditMs(m);
    setMsForm({ title: m.title, description: m.description || '', due_date: m.due_date ? m.due_date.slice(0,10) : '', status: m.status, completion_pct: m.completion_pct });
    setShowMsForm(true);
  };

  const saveMs = async () => {
    if (!msForm.title.trim()) return;
    setMsSaving(true);
    try {
      if (editMs) {
        await api.put(`/projects/${project.id}/milestones/${editMs.id}`, msForm);
      } else {
        await api.post(`/projects/${project.id}/milestones`, msForm);
      }
      setShowMsForm(false);
      loadMs();
    } catch (e) { alert(e.response?.data?.message || 'Save failed.'); }
    finally { setMsSaving(false); }
  };

  const deleteMs = async (id) => {
    if (!window.confirm('Delete this milestone?')) return;
    try { await api.delete(`/projects/${project.id}/milestones/${id}`); loadMs(); }
    catch { alert('Failed to delete milestone.'); }
  };

  const quickComplete = async (m) => {
    try {
      await api.put(`/projects/${project.id}/milestones/${m.id}`, { ...m, status:'completed', completion_pct:100 });
      loadMs();
    } catch {}
  };

  if (!project) return null;
  const isFrozen = !!project.frozen_at;

  // Milestone progress
  const total = milestones.length;
  const done  = milestones.filter(m => m.status === 'completed').length;
  const overallPct = total ? Math.round(done / total * 100) : 0;

  return (
    <Modal open onClose={onClose} title={`${project.project_number} — ${project.title}`} size="lg"
      footer={
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          {canEdit && !isFrozen && (
            <button className="btn btn-secondary" onClick={onEdit}>
              <EditIcon /> Edit
            </button>
          )}
          {canEdit && (
            <button className={`btn ${isFrozen ? 'btn-primary' : 'btn-warning'}`} onClick={onFreeze}>
              <FreezeIcon /> {isFrozen ? 'Unfreeze' : 'Freeze'}
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      }
    >
      {isFrozen && (
        <div style={{ marginBottom:12, padding:'8px 12px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, fontSize:'0.82rem', color:'#991b1b', fontWeight:600 }}>
          FROZEN — This project is locked. Frozen by {project.frozen_by_name || '—'} on {fmtDate(project.frozen_at)}.
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px 24px', marginBottom:16 }}>
        {[
          ['Type', cap(project.type)],
          ['Status', <span key="s" className={`badge badge-${statusColors[project.status] || 'neutral'}`}>{cap(project.status)}</span>],
          ['Priority', cap(project.priority)],
          ['Assigned To', project.assigned_to_name || '—'],
          ['Start Date', fmtDate(project.start_date)],
          ['End Date', fmtDate(project.end_date)],
          ['Budget', project.budget ? `${project.currency || 'INR'} ${Number(project.budget).toLocaleString('en-IN')}` : '—'],
        ].map(([label, val]) => (
          <div key={label}>
            <div style={{ fontSize:'0.72rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', marginBottom:2 }}>{label}</div>
            <div style={{ fontSize:'0.88rem', color:'var(--text-primary)' }}>{val}</div>
          </div>
        ))}
      </div>
      {project.deliverables && (
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:'0.72rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', marginBottom:4 }}>Deliverables</div>
          <div style={{ fontSize:'0.85rem', background:'var(--bg-secondary)', padding:'8px 12px', borderRadius:6, color:'var(--text-secondary)' }}>{project.deliverables}</div>
        </div>
      )}
      {project.notes && (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:'0.72rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', marginBottom:4 }}>Notes</div>
          <div style={{ fontSize:'0.85rem', background:'var(--bg-secondary)', padding:'8px 12px', borderRadius:6, color:'var(--text-secondary)' }}>{project.notes}</div>
        </div>
      )}

      {/* ── Milestones ─────────────────────────────────────────── */}
      <div style={{ borderTop:'1px solid #e2e8f0', paddingTop:14, marginTop:4 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:'0.75rem', fontWeight:800, color:'#1d4ed8', textTransform:'uppercase', letterSpacing:'0.08em' }}>Milestones</span>
            {total > 0 && (
              <span style={{ fontSize:'0.72rem', color:'#64748b' }}>{done}/{total} done</span>
            )}
            {total > 0 && (
              <div style={{ width:80, height:6, background:'#e2e8f0', borderRadius:3, overflow:'hidden' }}>
                <div style={{ width:`${overallPct}%`, height:'100%', background: overallPct === 100 ? '#10b981' : '#1d4ed8', borderRadius:3, transition:'width 0.3s' }} />
              </div>
            )}
          </div>
          {canEditMilestones && !isFrozen && (
            <button onClick={openAddMs}
              style={{ padding:'4px 12px', border:'1px solid #1d4ed8', borderRadius:6, background:'#eff6ff', color:'#1d4ed8', fontSize:'0.75rem', fontWeight:700, cursor:'pointer' }}>
              + Add
            </button>
          )}
        </div>

        {/* Add/Edit form */}
        {showMsForm && (
          <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:10, padding:'14px 16px', marginBottom:12 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 12px' }}>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ fontSize:'0.75rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Title *</label>
                <input className="form-control" value={msForm.title} onChange={e => setMsForm(f => ({ ...f, title:e.target.value }))} placeholder="Milestone title" />
              </div>
              <div>
                <label style={{ fontSize:'0.75rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Due Date</label>
                <input type="date" className="form-control" value={msForm.due_date} onChange={e => setMsForm(f => ({ ...f, due_date:e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize:'0.75rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Status</label>
                <select className="form-control form-select" value={msForm.status} onChange={e => setMsForm(f => ({ ...f, status:e.target.value }))}>
                  {Object.entries(MS_STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:'0.75rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Completion % ({msForm.completion_pct})</label>
                <input type="range" min={0} max={100} step={5} value={msForm.completion_pct}
                  onChange={e => setMsForm(f => ({ ...f, completion_pct: parseInt(e.target.value) }))}
                  style={{ width:'100%' }} />
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ fontSize:'0.75rem', fontWeight:600, color:'#64748b', display:'block', marginBottom:4 }}>Description</label>
                <textarea className="form-control" rows={2} value={msForm.description} onChange={e => setMsForm(f => ({ ...f, description:e.target.value }))} placeholder="Optional notes…" />
              </div>
            </div>
            <div style={{ display:'flex', gap:8, marginTop:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowMsForm(false)} style={{ padding:'6px 14px', border:'1px solid #e2e8f0', borderRadius:6, background:'#fff', color:'#64748b', fontSize:'0.8rem', cursor:'pointer' }}>Cancel</button>
              <button onClick={saveMs} disabled={msSaving || !msForm.title.trim()}
                style={{ padding:'6px 14px', border:'none', borderRadius:6, background:'#1d4ed8', color:'#fff', fontSize:'0.8rem', fontWeight:700, cursor:'pointer', opacity: msSaving ? 0.7 : 1 }}>
                {msSaving ? 'Saving…' : editMs ? 'Save Changes' : 'Add Milestone'}
              </button>
            </div>
          </div>
        )}

        {/* List */}
        {msLoading ? (
          <div style={{ textAlign:'center', padding:20, color:'#94a3b8', fontSize:'0.82rem' }}>Loading…</div>
        ) : milestones.length === 0 ? (
          <div style={{ textAlign:'center', padding:'20px 0', color:'#cbd5e1', fontSize:'0.82rem' }}>No milestones yet</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {milestones.map(m => {
              const ms = MS_STATUS[m.status] || MS_STATUS.pending;
              const isOverdue = m.due_date && m.status !== 'completed' && new Date(m.due_date) < new Date();
              return (
                <div key={m.id} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'10px 12px', background:'#fff', border:'1px solid #e2e8f0', borderRadius:8 }}>
                  {/* Status dot / quick-complete */}
                  <button title={m.status === 'completed' ? 'Completed' : 'Mark complete'}
                    onClick={() => m.status !== 'completed' && canEditMilestones && !isFrozen && quickComplete(m)}
                    style={{ marginTop:2, width:18, height:18, borderRadius:'50%', border:`2px solid ${ms.color}`, background: m.status==='completed' ? ms.color : 'transparent', flexShrink:0, cursor: m.status==='completed' ? 'default' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {m.status === 'completed' && <span style={{ color:'#fff', fontSize:10, fontWeight:900 }}>✓</span>}
                  </button>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <span style={{ fontWeight:600, fontSize:'0.85rem', color: m.status==='completed' ? '#94a3b8' : '#0f172a', textDecoration: m.status==='completed' ? 'line-through' : 'none' }}>{m.title}</span>
                      {m.created_by_name && (
                        <span style={{ fontSize:'0.72rem', color:'#64748b', fontWeight:500, background:'#f1f5f9', padding:'2px 6px', borderRadius:8 }}>
                          by {m.created_by_name}
                        </span>
                      )}
                      <span style={{ padding:'2px 8px', borderRadius:12, background:ms.bg, color:ms.color, fontSize:'0.68rem', fontWeight:700 }}>{ms.label}</span>
                      {isOverdue && <span style={{ fontSize:'0.68rem', color:'#dc2626', fontWeight:700 }}>OVERDUE</span>}
                    </div>
                    {m.description && <div style={{ fontSize:'0.77rem', color:'#64748b', marginTop:2 }}>{m.description}</div>}
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:4 }}>
                      {m.due_date && (
                        <span style={{ fontSize:'0.72rem', color: isOverdue ? '#dc2626' : '#94a3b8' }}>Due: {fmtDate(m.due_date)}</span>
                      )}
                      {m.completion_pct > 0 && m.status !== 'completed' && (
                        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                          <div style={{ width:50, height:4, background:'#e2e8f0', borderRadius:2, overflow:'hidden' }}>
                            <div style={{ width:`${m.completion_pct}%`, height:'100%', background:'#1d4ed8', borderRadius:2 }} />
                          </div>
                          <span style={{ fontSize:'0.68rem', color:'#64748b' }}>{m.completion_pct}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {canEditMilestones && !isFrozen && (
                    <div style={{ display:'flex', gap:4, flexShrink:0 }}>
                      <button onClick={() => openEditMs(m)} title="Edit"
                        style={{ padding:'3px 7px', border:'1px solid #e2e8f0', borderRadius:5, background:'#fff', color:'#64748b', fontSize:'0.72rem', cursor:'pointer' }}>✎</button>
                      <button onClick={() => deleteMs(m.id)} title="Delete"
                        style={{ padding:'3px 7px', border:'1px solid #fecaca', borderRadius:5, background:'#fff', color:'#dc2626', fontSize:'0.72rem', cursor:'pointer' }}>×</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ProjectAssignmentsSummary projectId={project?.id} />

      <EntityHistory entity="project" entityId={project?.id} />
    </Modal>
  );
}

export default function Projects() {
  const [data, setData]           = useState({ data: [], total: 0, page: 1, limit: 15 });
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatus] = useState('');
  const [typeFilter, setType]     = useState('');
  const [page, setPage]           = useState(1);
  const [users, setUsers]         = useState([]);
  const [viewProject, setView]    = useState(null);
  const [editProject, setEdit]    = useState(null);
  const [freezeProject, setFreeze]= useState(null);
  const [formOpen, setFormOpen]   = useState(false);

  // Assignment + Work Log modal state
  const [assignModal, setAssignModal] = useState({ open: false, projectId: null, projectNum: '', ownerId: null });
  const [assignments, setAssignments] = useState([]);
  const [workLogs, setWorkLogs]       = useState([]);
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignTab, setAssignTab]     = useState('assignments');
  const [assignForm, setAssignForm]   = useState({ user_id: '', access_level: 'read_only', notes: '' });
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError]   = useState('');
  const [wlForm, setWlForm]     = useState({ log_type: 'note', log_text: '' });
  const [wlSaving, setWlSaving] = useState(false);
  const [wlError, setWlError]   = useState('');

  const { isScience, isPrivileged, user } = useAuth();
  const canEdit = isScience; // Allow scientists to add/edit projects
  const canEditMilestones = user && !isPrivileged; // Allow regular users (not admin) to edit milestones

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/projects', {
        params: { page, limit: 15, search: debouncedSearch || undefined, status: statusFilter || undefined, type: typeFilter || undefined },
      });
      setData(res);
    } catch { setData({ data: [], total: 0, page: 1, limit: 15 }); }
    finally { setLoading(false); }
  }, [page, debouncedSearch, statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canEdit) return;
    api.get('/users').then(r => setUsers(r.data.data || r.data || [])).catch(() => {});
  }, [canEdit]);

  const openAssignModal = async (p) => {
    setAssignModal({ open: true, projectId: p.id, projectNum: p.project_number || p.title, ownerId: p.assigned_to });
    setAssignTab('assignments');
    setAssignForm({ user_id: '', access_level: 'read_only', notes: '' });
    setAssignError('');
    setWlForm({ log_type: 'note', log_text: '' });
    setWlError('');
    const [aRes, wlRes, usersRes] = await Promise.all([
      api.get(`/projects/${p.id}/assignments`),
      api.get(`/projects/${p.id}/work-logs`),
      api.get('/users', { params: { limit: 200 } }).catch(() => ({ data: { data: [] } })),
    ]);
    setAssignments(aRes.data.data || []);
    setWorkLogs(wlRes.data.data || []);
    setAssignUsers(usersRes.data.data || []);
  };

  const handleAddAssignment = async () => {
    if (!assignForm.user_id) { setAssignError('Please select a user.'); return; }
    setAssignSaving(true); setAssignError('');
    try {
      await api.post(`/projects/${assignModal.projectId}/assignments`, assignForm);
      const { data: r } = await api.get(`/projects/${assignModal.projectId}/assignments`);
      setAssignments(r.data || []);
      setAssignForm({ user_id: '', access_level: 'read_only', notes: '' });
    } catch (err) {
      setAssignError(err.response?.data?.message || 'Failed to add assignment.');
    } finally { setAssignSaving(false); }
  };

  const handleUpdateAssignment = async (assignId, access_level) => {
    try {
      await api.put(`/projects/${assignModal.projectId}/assignments/${assignId}`, { access_level });
      setAssignments(prev => prev.map(a => a.id === assignId ? { ...a, access_level } : a));
    } catch (err) {
      setAssignError(err.response?.data?.message || 'Failed to update assignment.');
    }
  };

  const handleRemoveAssignment = async (assignId, userName) => {
    if (!window.confirm(`Remove ${userName} from this project?`)) return;
    try {
      await api.delete(`/projects/${assignModal.projectId}/assignments/${assignId}`);
      setAssignments(prev => prev.filter(a => a.id !== assignId));
    } catch (err) {
      setAssignError(err.response?.data?.message || 'Failed to remove assignment.');
    }
  };

  const handleAddWorkLog = async () => {
    if (!wlForm.log_text.trim()) { setWlError('Please enter a log entry.'); return; }
    setWlSaving(true); setWlError('');
    try {
      await api.post(`/projects/${assignModal.projectId}/work-logs`, wlForm);
      const { data: r } = await api.get(`/projects/${assignModal.projectId}/work-logs`);
      setWorkLogs(r.data || []);
      setWlForm({ log_type: 'note', log_text: '' });
    } catch (err) {
      setWlError(err.response?.data?.message || 'Failed to add work log.');
    } finally { setWlSaving(false); }
  };

  const handleDeleteWorkLog = async (logId) => {
    if (!window.confirm('Delete this work log?')) return;
    try {
      await api.delete(`/projects/${assignModal.projectId}/work-logs/${logId}`);
      setWorkLogs(prev => prev.filter(l => l.id !== logId));
    } catch (err) {
      setWlError(err.response?.data?.message || 'Failed to delete work log.');
    }
  };

  const openCreate = () => { setEdit(null); setFormOpen(true); };
  const openEdit   = (p) => { setEdit(p); setFormOpen(true); setView(null); };

  const priorityDot = { low:'#94a3b8', medium:'#f59e0b', high:'#f97316', critical:'#ef4444' };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">CRO, synthesis, analytical and regulatory projects</p>
        </div>
        {canEdit && (
          <button className="btn btn-primary" onClick={openCreate}>
            + New Project
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '14px 16px' }}>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
            <div className="search-input-wrap" style={{ flex:'1 1 220px' }}>
              <span className="search-icon">
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input className="form-control" placeholder="Search project # or title…"
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-control form-select" style={{ width:140 }} value={statusFilter}
              onChange={e => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              {STATUSES.map(s => <option key={s} value={s}>{cap(s)}</option>)}
            </select>
            <select className="form-control form-select" style={{ width:160 }} value={typeFilter}
              onChange={e => { setType(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {TYPES.map(t => <option key={t} value={t}>{cap(t)}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Project #</th>
                <th>Title</th>
                <th>Type</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Start</th>
                <th>End</th>
                <th>Assigned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}
              {!loading && data.data.length === 0 && (
                <tr><td colSpan={10}><div className="empty-state"><p>No projects found.</p></div></td></tr>
              )}
              {!loading && data.data.map(p => {
                const isFrozen = !!p.frozen_at;
                return (
                  <tr key={p.id} style={isFrozen ? { opacity: 0.7 } : {}}>
                    <td>
                      <div style={{ fontWeight:700, color:'var(--primary)', fontSize:'0.82rem' }}>{p.project_number || p.project_code || '—'}</div>
                      {isFrozen && <div style={{ fontSize:'0.68rem', color:'#ef4444', fontWeight:700 }}>FROZEN</div>}
                    </td>
                    <td style={{ maxWidth:200 }}>
                      <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.title}</div>
                    </td>
                    <td><span className="badge badge-neutral" style={{ fontSize:'0.73rem' }}>{cap(p.type)}</span></td>
                    <td>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:'0.78rem', fontWeight:600, color: priorityDot[p.priority] || '#94a3b8' }}>
                        <span style={{ width:7, height:7, borderRadius:'50%', background: priorityDot[p.priority] || '#94a3b8', display:'inline-block' }}/>
                        {cap(p.priority)}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${statusColors[p.status] || 'neutral'}`} style={{ textTransform:'capitalize' }}>
                        {cap(p.status)}
                      </span>
                    </td>
                    <td style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>{fmtDate(p.start_date)}</td>
                    <td style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>{fmtDate(p.end_date)}</td>
                    <td style={{ fontSize:'0.82rem' }}>{p.assigned_to_name || '—'}</td>
                    <td>
                      <div className="table-actions">
                        <button className="btn btn-ghost btn-icon" data-tooltip="View" onClick={() => setView(p)}>
                          <EyeIcon />
                        </button>
                        {canEdit && !isFrozen && (
                          <button className="btn btn-ghost btn-icon" data-tooltip="Edit" onClick={() => openEdit(p)}>
                            <EditIcon />
                          </button>
                        )}
                        {canEdit && (
                          <button className="btn btn-ghost btn-icon" data-tooltip={isFrozen ? 'Unfreeze' : 'Freeze'}
                            onClick={() => setFreeze(p)}
                            style={{ color: isFrozen ? '#22c55e' : '#94a3b8' }}
                          >
                            <FreezeIcon />
                          </button>
                        )}
                        <button className="btn btn-ghost btn-icon" data-tooltip="Assignments & Work Logs"
                          style={{ color: 'var(--primary)' }}
                          onClick={() => openAssignModal(p)}>
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />
      </div>

      {/* Modals */}
      {viewProject && (
        <ViewModal
          project={viewProject}
          onClose={() => setView(null)}
          onEdit={() => openEdit(viewProject)}
          onFreeze={() => { setFreeze(viewProject); setView(null); }}
          canEdit={canEdit}
          canEditMilestones={canEditMilestones}
        />
      )}

      <ProjectFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={load}
        initial={editProject}
        users={users}
      />

      <FreezeModal
        open={!!freezeProject}
        project={freezeProject}
        onClose={() => setFreeze(null)}
        onDone={load}
      />

      {/* ── Assignments & Work Logs Modal ─────────────────────── */}
      <Modal open={assignModal.open}
        onClose={() => setAssignModal({ open: false, projectId: null, projectNum: '', ownerId: null })}
        title={`Assignments — ${assignModal.projectNum || ''}`} size="md"
        footer={<button className="btn btn-secondary" onClick={() => setAssignModal({ open: false, projectId: null, projectNum: '', ownerId: null })}>Close</button>}
      >
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
          {['assignments', 'worklogs'].map(tab => (
            <button key={tab} onClick={() => setAssignTab(tab)}
              style={{ padding: '7px 18px', background: 'none', border: 'none', borderBottom: assignTab === tab ? '2px solid var(--primary)' : '2px solid transparent', fontWeight: assignTab === tab ? 700 : 400, color: assignTab === tab ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>
              {tab === 'assignments' ? 'Assignments' : 'Work Logs'}
            </button>
          ))}
        </div>

        {assignTab === 'assignments' && (
          <div>
            {assignments.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '10px 0 16px' }}>No additional assignees yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {assignments.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{a.user_name}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {a.user_role} · Assigned by {a.assigned_by_name || '—'} · {new Date(a.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                      {a.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2, fontStyle: 'italic' }}>{a.notes}</div>}
                    </div>
                    {(isPrivileged || user?.id === assignModal.ownerId) ? (
                      <select className="form-control form-select" style={{ fontSize: '0.78rem', width: 130, padding: '3px 6px' }}
                        value={a.access_level}
                        onChange={e => handleUpdateAssignment(a.id, e.target.value)}>
                        <option value="read_only">Read Only</option>
                        <option value="read_write">Read + Write</option>
                      </select>
                    ) : (
                      <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 12, background: a.access_level === 'read_write' ? 'var(--primary-lighter)' : 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {a.access_level === 'read_write' ? 'Read + Write' : 'Read Only'}
                      </span>
                    )}
                    {(isPrivileged || user?.id === assignModal.ownerId) && (
                      <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--error)' }}
                        onClick={() => handleRemoveAssignment(a.id, a.user_name)}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add assignment form */}
            {(isPrivileged || user?.id === assignModal.ownerId ||
              assignments.some(a => a.user_id === user?.id && a.access_level === 'read_write')) && (
              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: 14, border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, fontSize: '0.83rem', marginBottom: 10 }}>Add Assignee</div>
                {assignError && <div className="alert alert-error" style={{ marginBottom: 10, fontSize: '0.8rem', padding: '6px 10px' }}>{assignError}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0, flex: 2, minWidth: 150 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>User</label>
                    <select className="form-control form-select" style={{ fontSize: '0.83rem' }}
                      value={assignForm.user_id}
                      onChange={e => setAssignForm(p => ({ ...p, user_id: e.target.value }))}>
                      <option value="">— Select user —</option>
                      {assignUsers
                        .filter(u => u.id !== assignModal.ownerId && !assignments.some(a => a.user_id === u.id))
                        .map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 130 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Access</label>
                    <select className="form-control form-select" style={{ fontSize: '0.83rem' }}
                      value={assignForm.access_level}
                      onChange={e => setAssignForm(p => ({ ...p, access_level: e.target.value }))}>
                      <option value="read_only">Read Only</option>
                      <option value="read_write">Read + Write</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, flex: 2, minWidth: 150 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Note (optional)</label>
                    <input className="form-control" style={{ fontSize: '0.83rem' }} placeholder="e.g. Handling synthesis…"
                      value={assignForm.notes}
                      onChange={e => setAssignForm(p => ({ ...p, notes: e.target.value }))} />
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={handleAddAssignment} disabled={assignSaving}
                    style={{ whiteSpace: 'nowrap', marginBottom: 2 }}>
                    {assignSaving ? 'Assigning…' : 'Assign'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {assignTab === 'worklogs' && (
          <div>
            {(isPrivileged || user?.id === assignModal.ownerId ||
              assignments.some(a => a.user_id === user?.id && a.access_level === 'read_write')) && (
              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: 14, border: '1px solid var(--border)', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: '0.83rem', marginBottom: 10 }}>Add Work Log</div>
                {wlError && <div className="alert alert-error" style={{ marginBottom: 10, fontSize: '0.8rem', padding: '6px 10px' }}>{wlError}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0, minWidth: 130 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Type</label>
                    <select className="form-control form-select" style={{ fontSize: '0.83rem' }}
                      value={wlForm.log_type}
                      onChange={e => setWlForm(p => ({ ...p, log_type: e.target.value }))}>
                      {['note','call','meeting','email','visit','follow_up','other'].map(t => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1).replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Entry *</label>
                    <input className="form-control" style={{ fontSize: '0.83rem' }} placeholder="Describe what was done…"
                      value={wlForm.log_text}
                      onChange={e => setWlForm(p => ({ ...p, log_text: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddWorkLog(); } }} />
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={handleAddWorkLog} disabled={wlSaving}
                    style={{ whiteSpace: 'nowrap', marginBottom: 2 }}>
                    {wlSaving ? 'Saving…' : 'Log'}
                  </button>
                </div>
              </div>
            )}

            {workLogs.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '10px 0' }}>No work logs yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {workLogs.map(log => (
                  <div key={log.id} style={{ display: 'flex', gap: 10, padding: '9px 12px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)', alignItems: 'flex-start' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary-lighter)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase' }}>{(log.user_name || '?')[0]}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>{log.user_name}</span>
                        <span style={{ fontSize: '0.72rem', padding: '1px 7px', borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                          {log.log_type.replace('_', ' ')}
                        </span>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                          {new Date(log.logged_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.83rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log.log_text}</div>
                    </div>
                    {(isPrivileged || log.user_id === user?.id || user?.id === assignModal.ownerId) && (
                      <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--error)', flexShrink: 0 }}
                        onClick={() => handleDeleteWorkLog(log.id)}>
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
